import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Robot, PaperPlaneRight, Plus, ChatCircleDots, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const SUGGESTIONS = [
  "Explique-moi simplement ce chapitre",
  "Donne-moi un exercice type examen",
  "Quelles sont les erreurs fréquentes à éviter ?",
  "Fais-moi un plan de révision pour la semaine",
];

export default function Tutor() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    const { data } = await api.get("/tutor/sessions");
    setSessions(data);
    return data;
  };

  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const openSession = async (id) => {
    setActiveId(id);
    const { data } = await api.get(`/tutor/sessions/${id}/messages`);
    setMessages(data);
  };

  const newSession = async () => {
    const { data } = await api.post("/tutor/sessions", { title: "Nouvelle conversation" });
    setSessions([data, ...sessions]);
    setActiveId(data.id);
    setMessages([]);
    return data.id;
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    let sid = activeId;
    if (!sid) sid = await newSession();

    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/tutor/sessions/${sid}/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: content }),
      });
      if (!res.ok || !res.body) throw new Error("stream error");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
      loadSessions();
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "⚠️ Le Professeur IA est momentanément indisponible. Réessayez plus tard." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-9rem)] gap-5 lg:h-[calc(100vh-6rem)]">
      {/* Sessions list */}
      <aside className="hidden w-64 shrink-0 flex-col rounded-2xl border border-border bg-white md:flex">
        <button data-testid="new-chat-btn" onClick={newSession} className="m-3 flex items-center justify-center gap-2 rounded-xl bg-terracotta py-2.5 text-sm font-semibold text-white hover:bg-[#E03E00]">
          <Plus weight="bold" /> Nouvelle conversation
        </button>
        <div className="no-scrollbar flex-1 space-y-1 overflow-y-auto px-3 pb-3">
          {sessions.map((s) => (
            <button key={s.id} data-testid={`session-${s.id}`} onClick={() => openSession(s.id)}
              className={`flex w-full items-center gap-2 truncate rounded-xl px-3 py-2.5 text-left text-sm transition ${activeId === s.id ? "bg-sand font-semibold" : "hover:bg-sand/60"}`}>
              <ChatCircleDots size={16} className="shrink-0 text-muted-foreground" /> <span className="truncate">{s.title}</span>
            </button>
          ))}
          {!sessions.length && <p className="px-3 py-4 text-center text-xs text-muted-foreground">Aucune conversation</p>}
        </div>
      </aside>

      {/* Chat */}
      <section className="flex flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-white">
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-terracotta/10 text-terracotta"><Robot weight="fill" size={22} /></div>
          <div>
            <div className="font-display font-bold">Professeur IA</div>
            <div className="text-xs text-muted-foreground">Tuteur pédagogique · Claude Sonnet 4.6</div>
          </div>
        </div>

        <div ref={scrollRef} className="no-scrollbar flex-1 space-y-4 overflow-y-auto p-5">
          {!messages.length && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="grid h-16 w-16 place-items-center rounded-2xl bg-terracotta/10 text-terracotta"><Sparkle weight="fill" size={32} /></div>
              <h2 className="mt-4 font-display text-xl font-bold">Bonjour {user?.name?.split(" ")[0]} !</h2>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">Posez n'importe quelle question sur votre programme. Je suis là pour expliquer, entraîner et corriger.</p>
              <div className="mt-6 grid max-w-lg gap-2 sm:grid-cols-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid="suggestion" onClick={() => send(s)} className="rounded-xl border border-border px-4 py-3 text-left text-sm transition hover:border-terracotta hover:bg-sand">{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "assistant" && <div className="mr-2 mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-terracotta/10 text-terracotta"><Robot weight="fill" size={16} /></div>}
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "rounded-tr-sm bg-terracotta text-white" : "rounded-tl-sm bg-cream text-ink"}`}>
                {m.role === "assistant"
                  ? (m.content ? <div className="prose-yaya"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown></div> : <span className="inline-flex gap-1"><span className="h-2 w-2 animate-bounce rounded-full bg-terracotta" /><span className="h-2 w-2 animate-bounce rounded-full bg-terracotta" style={{ animationDelay: "0.15s" }} /><span className="h-2 w-2 animate-bounce rounded-full bg-terracotta" style={{ animationDelay: "0.3s" }} /></span>)
                  : m.content}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-border p-3">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2">
            <textarea data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1} placeholder="Écrivez votre question…"
              className="max-h-32 flex-1 resize-none rounded-xl border border-border bg-cream px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-terracotta" />
            <button data-testid="chat-send" type="submit" disabled={streaming || !input.trim()}
              className="grid h-11 w-11 place-items-center rounded-xl bg-terracotta text-white transition hover:bg-[#E03E00] disabled:opacity-40">
              <PaperPlaneRight weight="fill" size={20} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
